/* eslint-disable i18next/no-literal-string -- .*/
import { useApproveCouponMutation } from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { FormField } from '@/components/base/form/form-field';
import { Input, InputVariants } from '@/components/base/form/input';
import { FormLabel } from '@/components/base/form/text-field';
import { PageHeading } from '@/components/base/page-heading';
import Section from '@/components/base/section';
import { cn } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { type FormEvent } from 'react';
import DatePicker from 'react-datepicker';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const couponSchema = z.object({
  couponCode: z.string().nonempty('Coupon code is required'),
  expirationDate: z.string().nonempty('Expiration date is required'),
  startDate: z.string().nonempty('Start Date date is required'),
  discountType: z.string().nonempty('Discount type is required'),
  amountType: z.string().nonempty('Amount type is required'),
  amount: z.string().min(1, 'Amount is required'),
});
type Coupon = z.infer<typeof couponSchema>;

export function ApproveCoupon(): JSX.Element {
  const [approveCoupon, { loading }] = useApproveCouponMutation();

  const methods = useForm<Coupon>({
    resolver: zodResolver(couponSchema),
    mode: 'onChange',
  });
  const { control, handleSubmit, getValues } = methods;

  async function onSubmit(): Promise<void> {
    const {
      couponCode,
      expirationDate,
      startDate,
      discountType,
      amountType,
      amount,
    } = getValues();
    try {
      await approveCoupon({
        variables: {
          couponCode,
          expirationDate,
          startDate,
          couponConfig: {
            discountType,
            amountType,
            amount: parseFloat(amount),
          },
        },
      });
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar({
          message: e.message,
          variant: 'error',
        });
      }
    }
  }

  function handleClickSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    return void (async () => {
      handleSubmit(onSubmit)();
    })();
  }

  return (
    <Section variant="secondary" className="max-w-3xl">
      <div className="flex w-full flex-col">
        <PageHeading>Approve Coupon</PageHeading>
        <FormProvider {...methods}>
          <form
            onSubmit={(e) => {
              handleClickSubmit(e);
            }}
            className="flex flex-col gap-5"
          >
            <div className="flex w-full flex-col gap-5 sm:flex-row">
              <fieldset className="w-full">
                <FormField label="Coupon Code" name="couponCode">
                  <Input
                    variant="secondary"
                    type="text"
                    placeholder="Coupon Code"
                  />
                </FormField>
              </fieldset>

              <fieldset className="w-full">
                <FormField label="Amount" name="amount">
                  <Input
                    variant="secondary"
                    type="number"
                    placeholder="Amount"
                  />
                </FormField>
              </fieldset>
            </div>

            <fieldset>
              <FormField label="Amount Type" name="amountType">
                <Input
                  variant="secondary"
                  type="text"
                  placeholder="Amount Type"
                />
              </FormField>
            </fieldset>

            <fieldset>
              <FormField label="Discount Type" name="discountType">
                <Input
                  variant="secondary"
                  type="text"
                  placeholder="Discount Type"
                />
              </FormField>
            </fieldset>

            <div className="flex w-full flex-col gap-5 sm:flex-row">
              <fieldset className="w-full">
                <div className="flex flex-col">
                  <FormLabel id="startDate" label="Start Date" />
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) => {
                          field.onChange(date ? date.toISOString() : '');
                        }}
                        value={
                          field.value
                            ? format(new Date(field.value), 'dd.MM.yyyy')
                            : ''
                        }
                        dateFormat="dd.MM.yyyy"
                        className={cn(InputVariants({ variant: 'secondary' }))}
                        placeholderText="Select a date"
                      />
                    )}
                  />
                </div>
              </fieldset>

              <fieldset className="w-full">
                <div className="flex flex-col">
                  <FormLabel id="expirationDate" label="Expiration Date" />
                  <Controller
                    name="expirationDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) => {
                          field.onChange(date ? date.toISOString() : '');
                        }}
                        value={
                          field.value
                            ? format(new Date(field.value), 'dd.MM.yyyy')
                            : ''
                        }
                        dateFormat="dd.MM.yyyy"
                        className={cn(InputVariants({ variant: 'secondary' }))}
                        placeholderText="Select a date"
                      />
                    )}
                  />
                </div>
              </fieldset>
            </div>

            <div className="flex w-full justify-end">
              <Button loading={loading} type="submit">
                Approve
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Section>
  );
}
